import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  Megaphone, Sparkle, Article, CalendarCheck, CheckCircle, XCircle, ShareNetwork,
  MagicWand, Package, Eye, PlugsConnected, ArrowClockwise, CaretRight, Clock,
} from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { PromoPageHeader } from "../components/PromoPageHeader";
import { KpiCard } from "../components/KpiCard";
import { EmptyState } from "../components/EmptyState";
import { ConnectionHealth } from "../components/ConnectionHealth";
import { StatusBadge, StatusDot } from "../components/StatusBadge";
import { ProgressBar } from "../components/campaign/ProgressBar";
import { CampaignCard } from "../components/campaign/CampaignCard";
import { useDashboardData, useConnectionHealth } from "../usePromo";
import { MARKETPLACES, PLATFORM_BY_KEY } from "../platforms";
import { estimateFromPromo, isToday, fmtDateTime, fmtDuration, fmtRelative } from "../campaignUtils";

const QUICK_ACTIONS = [
  { key: "new", to: "/promotions/new", Icon: MagicWand, ai: true },
  { key: "campaigns", to: "/promotions/campaigns", Icon: Megaphone },
  { key: "catalog", to: "/promotions/catalog", Icon: Package },
  { key: "preview", to: "/promotions/preview", Icon: Eye, soon: true },
  { key: "connections", to: "/promotions/connections", Icon: PlugsConnected },
];

export default function PromotionHome() {
  const { t } = useTranslation();
  const { promotions, posts, events, accounts, loading, reload } = useDashboardData();
  const conn = useConnectionHealth();

  const accountsById = useMemo(() => Object.fromEntries(accounts.map((a) => [a.id, a])), [accounts]);
  const campaignByCid = useMemo(
    () => Object.fromEntries(promotions.filter((p) => p.social_campaign_id).map((p) => [p.social_campaign_id, p])),
    [promotions],
  );

  const m = useMemo(() => {
    const active = promotions.filter((p) => p.status === "active");
    const drafts = promotions.filter((p) => p.status === "draft");
    const scheduled = posts.filter((p) => ["scheduled", "pending"].includes(p.status));
    const publishedToday = posts.filter((p) => p.status === "published" && isToday(p.published_at));
    const publishedAll = posts.filter((p) => p.status === "published");
    const failed = posts.filter((p) => p.status === "failed");
    const nextScheduled = [...scheduled].filter((p) => p.scheduled_at)
      .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
    const activeNext = active.map((p) => p.next_run_at).filter(Boolean).sort()[0];
    const mkConnected = MARKETPLACES.filter((mk) => conn.sources.some((s) => s.connector_id === mk.key && s.status === "connected")).length;
    const scConnected = new Set(conn.accounts.filter((a) => a.status === "connected").map((a) => a.platform)).size;
    return { active, drafts, scheduled, publishedToday, publishedAll, failed, nextScheduled, activeNext, mkConnected, scConnected };
  }, [promotions, posts, conn.sources, conn.accounts]);

  const platformOf = (post) => {
    const acc = accountsById[(post.account_ids || [])[0]];
    return acc?.platform || null;
  };

  return (
    <div className="space-y-8" data-testid="promotion-dashboard">
      <PromoPageHeader
        overline={t("promo.home.overline")}
        title={t("promo.dash.title")}
        description={t("promo.dash.subtitle")}
        actions={
          <div className="flex items-center gap-2">
            <button onClick={reload} className="flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground" data-testid="dash-refresh">
              <ArrowClockwise size={14} className={loading ? "animate-spin" : ""} /> {t("promo.conn.refresh")}
            </button>
            <Link to="/promotions/new" className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90" data-testid="dash-new-campaign">
              <MagicWand size={16} weight="fill" /> {t("promo.nav.new")}
            </Link>
          </div>
        }
      />

      {/* Executive KPIs */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7" data-testid="kpi-grid">
        <KpiCard index={0} testId="kpi-campaigns" label={t("promo.dash.kpi.campaigns")} value={promotions.length} Icon={Megaphone} hint={t("promo.dash.kpi.activeHint", { n: m.active.length })} />
        <KpiCard index={1} testId="kpi-active" label={t("promo.dash.kpi.active")} value={m.active.length} Icon={Sparkle} hint={m.activeNext ? t("promo.dash.kpi.nextHint", { v: fmtRelative(m.activeNext) }) : t("promo.dash.kpi.noneRunning")} />
        <KpiCard index={2} testId="kpi-draft" label={t("promo.dash.kpi.draft")} value={m.drafts.length} Icon={Article} hint={t("promo.dash.kpi.draftHint")} />
        <KpiCard index={3} testId="kpi-scheduled" label={t("promo.dash.kpi.scheduled")} value={m.scheduled.length} Icon={CalendarCheck} hint={m.nextScheduled[0] ? t("promo.dash.kpi.nextHint", { v: fmtDateTime(m.nextScheduled[0].scheduled_at) }) : t("promo.dash.kpi.noQueue")} />
        <KpiCard index={4} testId="kpi-published" label={t("promo.dash.kpi.publishedToday")} value={m.publishedToday.length} Icon={CheckCircle} hint={t("promo.dash.kpi.totalHint", { n: m.publishedAll.length })} />
        <KpiCard index={5} testId="kpi-failed" label={t("promo.dash.kpi.failed")} value={m.failed.length} Icon={XCircle} hint={m.failed.length ? t("promo.dash.kpi.needsAttention") : t("promo.dash.kpi.allGood")} />
        <KpiCard index={6} testId="kpi-connections" label={t("promo.dash.kpi.connections")} value={m.mkConnected + m.scConnected} Icon={ShareNetwork} hint={t("promo.dash.kpi.connHint", { mk: m.mkConnected, sc: m.scConnected })} />
      </div>

      {/* Quick actions */}
      <div className="flex flex-wrap gap-2" data-testid="quick-actions">
        {QUICK_ACTIONS.map(({ key, to, Icon, ai, soon }) => (
          <Link
            key={key} to={soon ? "#" : to} onClick={soon ? (e) => e.preventDefault() : undefined}
            data-testid={`qa-${key}`}
            className={cn(
              "flex items-center gap-2 rounded-md border px-3.5 py-2 text-sm font-medium transition-colors",
              soon ? "cursor-not-allowed border-border text-muted-foreground/60"
                : ai ? "border-ai/30 bg-ai/10 text-ai hover:bg-ai/20"
                  : "border-border bg-card hover:bg-accent/40",
            )}
          >
            <Icon size={16} weight="duotone" /> {t(`promo.home.qs.${key}.title`)}
            {soon && <span className="rounded-full border border-border px-1.5 py-px text-[9px] uppercase tracking-wider">{t("promo.nav.soon")}</span>}
          </Link>
        ))}
      </div>

      {/* Campaign Summary + Next Publications */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <section className="space-y-4 xl:col-span-2" data-testid="campaign-summary">
          <SectionHeader title={t("promo.dash.summary")} to="/promotions/campaigns" t={t} />
          {m.active.length === 0 ? (
            <EmptyState Icon={Megaphone} title={t("promo.dash.noActive")} description={t("promo.dash.noActiveDesc")}
              action={<Link to="/promotions/new" className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground">{t("promo.nav.new")}</Link>} />
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {m.active.slice(0, 4).map((p, i) => <CampaignCard key={p.id} promo={p} index={i} />)}
            </div>
          )}
        </section>

        <section className="space-y-4" data-testid="next-publications">
          <SectionHeader title={t("promo.dash.nextPublications")} t={t} />
          <div className="rounded-lg border border-border bg-card p-2">
            {m.nextScheduled.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">{t("promo.dash.noUpcoming")}</div>
            ) : (
              <ul className="divide-y divide-border/60">
                {m.nextScheduled.slice(0, 6).map((post) => {
                  const plat = platformOf(post);
                  const meta = plat ? PLATFORM_BY_KEY[plat] : null;
                  const camp = campaignByCid[post.campaign_id];
                  return (
                    <li key={post.id} className="flex items-center gap-3 px-2 py-3" data-testid={`next-pub-${post.id}`}>
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border bg-accent/40">
                        {meta ? <meta.Icon size={14} /> : <Clock size={14} className="text-muted-foreground" />}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{(post.content || "").split("\n")[0] || camp?.name || "—"}</p>
                        <p className="truncate text-xs text-muted-foreground">{camp?.name || ""}</p>
                      </div>
                      <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{fmtDateTime(post.scheduled_at)}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </section>
      </div>

      {/* Live Campaign Overview */}
      <section className="space-y-4" data-testid="live-overview">
        <SectionHeader title={t("promo.dash.liveOverview")} to="/promotions/campaigns" t={t} />
        <LiveOverviewTable promotions={promotions.filter((p) => p.status !== "draft")} t={t} />
      </section>

      {/* Activity feeds */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ActivityCard title={t("promo.dash.todayActivity")} events={events.filter((e) => isToday(e.created_at)).slice(0, 8)} empty={t("promo.dash.noToday")} t={t} testId="today-activity" />
        <ActivityCard title={t("promo.dash.recentActivity")} events={events.slice(0, 8)} empty={t("promo.detail.noEvents")} t={t} testId="recent-activity" />
      </div>

      {/* Connection Health */}
      <section data-testid="dash-connection-health">
        <ConnectionHealth title={t("promo.conn.title")} />
      </section>
    </div>
  );
}

function SectionHeader({ title, to, t }) {
  return (
    <div className="flex items-center justify-between">
      <h3 className="text-lg font-medium tracking-tight">{title}</h3>
      {to && (
        <Link to={to} className="flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground">
          {t("promo.dash.viewAll")} <CaretRight size={12} weight="bold" />
        </Link>
      )}
    </div>
  );
}

function LiveOverviewTable({ promotions, t }) {
  if (!promotions.length) {
    return <EmptyState Icon={Sparkle} title={t("promo.dash.noRunning")} description={t("promo.dash.noRunningDesc")} />;
  }
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="promo-glass border-b border-border">
            <tr>
              {["campaign", "status", "progress", "remaining", "eta", "next"].map((h) => (
                <th key={h} className={cn("px-4 py-3 promo-overline whitespace-nowrap", h === "campaign" ? "text-left" : "text-left")}>
                  {t(`promo.dash.col.${h}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {promotions.map((p) => {
              const e = estimateFromPromo(p);
              return (
                <motion.tr
                  key={p.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  className="border-b border-border/60 last:border-0 hover:bg-accent/30" data-testid={`live-row-${p.id}`}
                >
                  <td className="px-4 py-3">
                    <Link to={`/promotions/campaigns/${p.id}`} className="flex items-center gap-2 font-medium hover:text-brand">
                      <StatusDot status={p.status === "active" ? "active" : p.status} /> {p.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={p.status} label={t(`promo.campaigns.status.${p.status}`)} /></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <ProgressBar value={e.pct} tone={p.status === "completed" ? "success" : "brand"} className="w-24" />
                      <span className="tabular-nums text-xs text-muted-foreground">{e.pct}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 tabular-nums">{e.remaining}</td>
                  <td className="px-4 py-3 tabular-nums text-muted-foreground">{fmtDuration(e.etaMs)}</td>
                  <td className="px-4 py-3 tabular-nums text-muted-foreground">{fmtDateTime(e.nextPublishAt)}</td>
                </motion.tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ActivityCard({ title, events, empty, t, testId }) {
  return (
    <div className="rounded-lg border border-border bg-card p-5" data-testid={testId}>
      <h3 className="mb-4 text-sm font-medium">{title}</h3>
      {events.length === 0 ? (
        <p className="text-sm text-muted-foreground">{empty}</p>
      ) : (
        <div className="relative space-y-3 border-l border-border pl-5">
          {events.map((e) => (
            <div key={e.id} className="relative" data-testid={`activity-${e.id}`}>
              <span className="absolute -left-[23px] top-1 h-1.5 w-1.5 rounded-full bg-brand" />
              <div className="flex items-center justify-between gap-3">
                <span className="truncate font-mono text-xs">{e.type}</span>
                <span className="shrink-0 text-xs text-muted-foreground">{fmtRelative(e.created_at)}</span>
              </div>
              {e.actor_email && <p className="truncate text-[11px] text-muted-foreground">{e.actor_email}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
